import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { faClose, faSliders } from "@fortawesome/free-solid-svg-icons"
import ModalPortal from "../../../components/Modal/ModalPortal"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import GetSpace from "../../../utils/space"
import { companyMemory, servicePointMemory } from "../../../Objects/Memory/Memory"
import { useCreateCompany } from "../../../hooks/Company/useCreateCompany"
import { useSaveCompany } from "../../../hooks/Company/useSaveCompany"
import { SimpleToggle } from "../../../components/SimpleToggle/SimpleToggle"
import { CanView } from "../../../components/CanView/CanView"
import { Permissions } from "../../../Enum/PermissionsType"
import { useRegister } from "../../../hooks/Register/useRegister"
import { ViewRegister } from "../../../components/ViewRegister/ViewRegister"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import { usePermission } from "../../../hooks/Permissions/usePermission"
import { EDeliveryFulfillmentType } from "../../../Enum/EDeliveryFulfillmentType"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import { EEnableStatusType } from "../../../Enum/EEnableStatusType"
import { EZeroValueSelectionOptionDisplayMode } from "../../../Enum/EZeroValueSelectionOptionDisplayMode"

interface OperationalSettingsPopupProps {
  onClose: () => void
}

export const OperationalSettingsPopup: React.FC<OperationalSettingsPopupProps> = ({ onClose }) => {
  const { t } = useTranslation("company")

  const canEdite = !usePermission(Permissions.CanEditeCompany)

  const { handleSaveCompany, handleSaveServicePointSelfService, handleSaveDefaultServicePoint } = useSaveCompany()

  const { handleEnableRegister, handleDisableRegister, isRegisterEnable } = useRegister()

  const [isServicePointSelfServiceEnabled, setServicePointSelfServiceEnabled] = useState(
    companyMemory.isServicePointSelfServiceEnabled,
  )

  const [isServicePointSelfServiceOrderingEnabled, setServicePointSelfServiceOrderingEnabled] = useState(
    companyMemory.isServicePointSelfServiceOrderingEnabled,
  )

  const [defaultServicePointId, setDefaultServicePointId] = useState<number | null>(companyMemory.defaultServicePointId)

  const activeServicePoints = servicePointMemory.listData
    .filter((servicePoint) => servicePoint.status === EEnableStatusType.Active)
    .sort((first, second) => first.name.localeCompare(second.name))

  const {
    handleInitialValue,
    handleSubmitForm,
    isDeliveryOrderNotificationEnabled,
    deliveryFulfillmentType,
    zeroValueSelectionOptionDisplayMode,
    setIsDeliveryOrderNotificationEnabled,
    setDeliveryFulfillmentType,
    setZeroValueSelectionOptionDisplayMode,
  } = useCreateCompany()

  useEffect(() => {
    handleInitialValue(companyMemory)
    setServicePointSelfServiceEnabled(companyMemory.isServicePointSelfServiceEnabled)
    setServicePointSelfServiceOrderingEnabled(companyMemory.isServicePointSelfServiceOrderingEnabled)
    setDefaultServicePointId(
      servicePointMemory.listData.some(
        (servicePoint) =>
          servicePoint.id === companyMemory.defaultServicePointId && servicePoint.status === EEnableStatusType.Active,
      )
        ? companyMemory.defaultServicePointId
        : null,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleTrySave = async () => {
    const didSaveCompany = await handleSaveCompany(handleSubmitForm(), () => {})

    if (!didSaveCompany) {
      return
    }

    const didSaveDefaultServicePoint = await handleSaveDefaultServicePoint(companyMemory.id, defaultServicePointId)

    if (!didSaveDefaultServicePoint) {
      return
    }

    await handleSaveServicePointSelfService(
      companyMemory.id,
      isServicePointSelfServiceEnabled,
      isServicePointSelfServiceOrderingEnabled,
      () => {
        handleInitialValue(companyMemory)
        onClose()
      },
    )
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={t("operational.title")}
              icon={faSliders}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className="flex items-center gap-1">
                  <PopupHelpButton
                    helpKey="pages.mystore.mycompany.operationalsettingspopup.1"
                    variant="global"
                    title={t("helpPopup:global.operationalSettings.title")}
                    description={t("helpPopup:global.operationalSettings.description")}
                    bullets={t("helpPopup:global.operationalSettings.bullets", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.operationalSettingsPopup.button.onClose"
                  />
                </div>
              }
            />

            <GetSpace top={10} />

            <form
              className="flex flex-col gap-4 p-2"
              onSubmit={(e) => {
                e.preventDefault()
                e.stopPropagation()

                void handleTrySave()
              }}
            >
              <GenericDropDown
                legend={t("operational.delivery_fulfillment_mode")}
                initialState={deliveryFulfillmentType.toString()}
                data={[
                  {
                    value: EDeliveryFulfillmentType.DeliveryOnly.toString(),
                    label: t("operational.delivery_mode_delivery_only"),
                  },
                  {
                    value: EDeliveryFulfillmentType.PickupOnly.toString(),
                    label: t("operational.delivery_mode_pickup_only"),
                  },
                  {
                    value: EDeliveryFulfillmentType.Both.toString(),
                    label: t("operational.delivery_mode_both"),
                  },
                ]}
                disable={canEdite}
                handlerFuncion={(event) => {
                  setDeliveryFulfillmentType(Number(event.target.value) as EDeliveryFulfillmentType)
                }}
                descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.operationalSettingsPopup.select.deliveryFulfillmentMode"
              />

              <SimpleToggle
                animationDuration="1.6s"
                title={t("operational.delivery_email_notification_description")}
                value={isDeliveryOrderNotificationEnabled}
                disabled={canEdite}
                onChange={(value) => setIsDeliveryOrderNotificationEnabled(value)}
                descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.operationalSettingsPopup.toggle.deliveryEmailNotificationDescription"
              />

              <div className={defaultStyle.divisionBar}></div>

              <GenericDropDown
                legend={t("operational.zero_value_option_display.label")}
                initialState={zeroValueSelectionOptionDisplayMode.toString()}
                data={[
                  {
                    value: EZeroValueSelectionOptionDisplayMode.ShowFree.toString(),
                    label: t("operational.zero_value_option_display.mode_show_free"),
                  },
                  {
                    value: EZeroValueSelectionOptionDisplayMode.ShowNothing.toString(),
                    label: t("operational.zero_value_option_display.mode_show_nothing"),
                  },
                  {
                    value: EZeroValueSelectionOptionDisplayMode.ShowBasePrice.toString(),
                    label: t("operational.zero_value_option_display.mode_show_base_price"),
                  },
                ]}
                disable={canEdite}
                handlerFuncion={(event) => {
                  setZeroValueSelectionOptionDisplayMode(
                    Number(event.target.value) as EZeroValueSelectionOptionDisplayMode,
                  )
                }}
                descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.operationalSettingsPopup.select.zeroValueSelectionOptionDisplayMode"
              />

              <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                <p className="px-2 text-xs text-slate-500">{t("operational.zero_value_option_display.hint")}</p>
              </Animate>

              <div className={defaultStyle.divisionBar}></div>

              <GenericDropDown
                legend={t("operational.default_service_point.label")}
                initialState={defaultServicePointId?.toString() ?? ""}
                data={[
                  { value: "", label: t("operational.default_service_point.none") },
                  ...activeServicePoints.map((servicePoint) => ({
                    value: servicePoint.id.toString(),
                    label: t("operational.default_service_point.option", { name: servicePoint.name }),
                  })),
                ]}
                disable={canEdite}
                handlerFuncion={(event) => {
                  setDefaultServicePointId(event.target.value ? Number(event.target.value) : null)
                }}
                descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.operationalSettingsPopup.select.defaultServicePoint"
              />

              <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                <p className="px-2 text-xs text-slate-500">{t("operational.default_service_point.hint")}</p>
              </Animate>

              <div className={defaultStyle.divisionBar}></div>

              <SimpleToggle
                title={t("operational.service_point_self_service.enabled")}
                value={isServicePointSelfServiceEnabled}
                disabled={canEdite}
                onChange={setServicePointSelfServiceEnabled}
                descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.operationalSettingsPopup.toggle.servicePointSelfServiceEnabled"
              />

              <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                <p className="px-2 text-xs text-slate-500">
                  {t("operational.service_point_self_service.enabled_hint")}
                </p>
              </Animate>

              {isServicePointSelfServiceEnabled && (
                <>
                  <SimpleToggle
                    title={t("operational.service_point_self_service.ordering_enabled")}
                    value={isServicePointSelfServiceOrderingEnabled}
                    disabled={canEdite}
                    onChange={setServicePointSelfServiceOrderingEnabled}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.operationalSettingsPopup.toggle.servicePointSelfServiceOrderingEnabled"
                  />

                  <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                    <p className="px-2 text-xs text-slate-500">
                      {t("operational.service_point_self_service.ordering_enabled_hint")}
                    </p>
                  </Animate>
                </>
              )}

              <div className={defaultStyle.divisionBar}></div>

              <section className={defaultStyle.buttonsController}>
                <CanView permission={Permissions.CanEditeCompany}>
                  <Button
                    label={t("buttons:button_save")}
                    typeButton="submit"
                    type="primary"
                    size="medium"
                    bgColor={THEME.colors.buttonBGSaveColor}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.operationalSettingsPopup.button.save"
                  />
                </CanView>

                <GetSpace right={5} />

                <Button
                  label={t("buttons:button_register")}
                  typeButton="button"
                  type="primary"
                  size="medium"
                  bgColor={THEME.colors.buttonBGRegisterColor}
                  onClick={handleEnableRegister}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.operationalSettingsPopup.button.register"
                />
              </section>
            </form>

            {isRegisterEnable && (
              <ViewRegister id={companyMemory.id} typeEntity="CompanyEntityId" onClose={handleDisableRegister} />
            )}
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
